import { createContext, ReactNode, useContext, useMemo } from 'react'
import { useSignOutScopedFlag } from 'shared/hooks/use-sign-out-scoped-flag'
import { isTwoFactorEnabled } from 'shared/utils'

const STORAGE_KEY = 'twoFactorPromptVisible'

interface TwoFactorPromptContextValue {
  visible: boolean
  showTwoFactorPrompt: () => void
  hideTwoFactorPrompt: () => void
}

const TwoFactorPromptContext =
  createContext<TwoFactorPromptContextValue | null>(null)

interface Props {
  children: ReactNode
}

export const TwoFactorPromptProvider = ({ children }: Props) => {
  // The prompt is persisted, while 2FA may have been turned on in another browser
  const {
    isSet: visible,
    set: showTwoFactorPrompt,
    clear: hideTwoFactorPrompt,
  } = useSignOutScopedFlag(STORAGE_KEY, isTwoFactorEnabled)

  const value = useMemo(
    () => ({ visible, showTwoFactorPrompt, hideTwoFactorPrompt }),
    [visible, showTwoFactorPrompt, hideTwoFactorPrompt]
  )

  return (
    <TwoFactorPromptContext.Provider value={value}>
      {children}
    </TwoFactorPromptContext.Provider>
  )
}

export const useTwoFactorPromptState = () => {
  const context = useContext(TwoFactorPromptContext)

  if (!context) {
    throw new Error(
      'useTwoFactorPromptState must be used inside TwoFactorPromptProvider'
    )
  }

  return context
}
